import { QueryClient } from "@/lib/ReactQuery/QueryClient";
import { organisationsQueryOptions } from "@/lib/Auth/organisationsQuery";
import { useAuthStore } from "@/lib/Auth/authStore";

/**
 * The tenant prefix in the scoped URL `/$tenant/$orgId/supplier/{-$supplierId}`.
 * `admin` (admin orgs) or `org` (supplier orgs, and admins viewing as a tenant)
 * — the synchronous source of truth for admin-ness.
 */
export type Tenant = "admin" | "org";

/**
 * Pick the tenant prefix for an org from the *cached* organisations query only —
 * never fetches, so it can't block the entry redirect. When the cache is cold
 * (fresh login / hard reload) the org is unknown and we optimistically return
 * `org`; AuthProvider resolves `/organisations` in the background and the
 * `router.invalidate()` in InnerApp re-runs the guards to correct an admin into
 * the `/admin` tree. Returns `admin` for admin orgs, `org` otherwise.
 */
export function resolveTenantForOrg(orgId: string | null): Tenant {
  if (!orgId) {
    return "org";
  }
  const token = useAuthStore.getState().token;
  const data = QueryClient.getQueryData<{
    organisations?: { id: string; is_admin: boolean }[];
  } | null>(organisationsQueryOptions(token).queryKey);
  const org = data?.organisations?.find((o) => o.id === orgId);
  return org?.is_admin ? "admin" : "org";
}
